import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Upload, FileSearch, CheckCircle2, AlertCircle, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { TARGET_ROLE_GROUPS } from "../constants/targetRoles";
import SummaryRewrite from "../components/SummaryRewrite";

export default function ResumeAnalyzer() {
  const [file, setFile] = useState(null);
  const [role, setRole] = useState("Software Engineer");
  const [uploading, setUploading] = useState(false);
  const [progressLabel, setProgressLabel] = useState("");
  const [progress, setProgress] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [resumeId, setResumeId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const inputRef = useRef();
  const pollRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const MAX_POLL_ATTEMPTS = 90; // ~90 polls × 2.5s ≈ 3.75 min upper cap

  const loadHistory = async () => {
    const r = await api.get("/resume/list");
    setHistory(r.data.items || []);
  };
  useEffect(() => { loadHistory(); return () => { if (pollRef.current) clearTimeout(pollRef.current); }; }, []);

  // Progress labels cycle to give users something to look at while the AI runs.
  const PROGRESS_STAGES = [
    { until: 0.25, label: "Reading your resume…" },
    { until: 0.50, label: "Extracting skills & experience…" },
    { until: 0.80, label: "Scoring against target role…" },
    { until: 1.00, label: "Finalizing recommendations…" },
  ];
  const labelFor = (p) => (PROGRESS_STAGES.find(s => p <= s.until) || PROGRESS_STAGES[3]).label;

  const pollJob = async (jid) => {
    try {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        setUploading(false);
        toast.error("Analysis is taking too long. Please try again.");
        return;
      }
      const r = await api.get(`/resume/jobs/${jid}`);
      const { job, analysis: a } = r.data;
      const p = Math.max(0.1, Math.min(0.95, job?.progress ?? 0.3));
      setProgress(p);
      setProgressLabel(labelFor(p));
      if (job?.status === "completed" && a) {
        setAnalysis(a);
        setResumeId(a.resume_id || null);
        setProgress(1); setProgressLabel("Complete");
        setUploading(false);
        toast.success("Resume analyzed!");
        loadHistory();
        return;
      }
      if (job?.status === "failed") {
        setUploading(false);
        toast.error(job.error ? `Analysis failed: ${job.error}` : "Analysis failed. Please try again.");
        return;
      }
      // keep polling
      pollRef.current = setTimeout(() => pollJob(jid), 2500);
    } catch (e) {
      // Transient network: back off and retry; cap retries via MAX_POLL_ATTEMPTS.
      pollRef.current = setTimeout(() => pollJob(jid), 4000);
    }
  };

  const onUpload = async () => {
    if (!file) { toast.error("Choose a resume file first"); return; }
    if (uploading) return; // prevent double-click
    setUploading(true); setAnalysis(null); setProgress(0.08);
    setProgressLabel("Uploading resume…");
    pollAttemptsRef.current = 0;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_role", role);
      const r = await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { job_id, resume_id, deduped } = r.data;
      setJobId(job_id); setResumeId(resume_id);
      if (deduped) toast.info("Resuming your previous analysis…");
      setProgressLabel("AI analysis queued…");
      pollJob(job_id);
    } catch (e) {
      setUploading(false);
      apiErrorToast(e, "Upload failed");
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="resume-analyzer-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Resume Analyzer</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Score your resume in under 2 minutes.</h1>
        <p className="text-zinc-600 mt-2 text-sm max-w-2xl">Upload your PDF or DOCX. Our AI grades you across 6 dimensions like a senior recruiter would, then rewrites your summary for the target role on demand.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-flat p-6 lg:col-span-1">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">1. Upload</div>
          <label htmlFor="resume-file" className="mt-4 border-2 border-dashed border-zinc-300 rounded-lg p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#0E7C66] hover:bg-[#0E7C66]/[0.02] transition" data-testid="resume-dropzone">
            <Upload size={28} className="text-zinc-400" />
            <div className="font-semibold mt-3 text-sm">{file ? file.name : "Click to choose file"}</div>
            <div className="text-xs text-zinc-500 mt-1">PDF, DOC, DOCX · Max 5MB</div>
            <input id="resume-file" data-testid="resume-file-input" ref={inputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e)=>setFile(e.target.files?.[0]||null)} />
          </label>

          <div className="mt-4">
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Target role</label>
            <select data-testid="resume-target-role" className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md bg-white" value={role} onChange={(e)=>setRole(e.target.value)}>
              {TARGET_ROLE_GROUPS.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.roles.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          <button data-testid="resume-analyze-btn" onClick={onUpload} disabled={uploading || !file} className="mt-4 w-full h-11 rounded-md bg-[#0E7C66] text-white font-semibold hover:bg-[#0A5E4D] transition flex items-center justify-center gap-2 disabled:opacity-50">
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Analyzing…</> : <>Analyze <ArrowRight size={14} /></>}
          </button>

          {history.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">History</div>
              <div className="space-y-2">
                {history.slice(0,5).map(h => (
                  <button key={h.id} onClick={()=>setAnalysis(h)} className="w-full text-left p-3 rounded-md border border-zinc-200 hover:border-zinc-900 transition" data-testid={`resume-history-${h.id}`}>
                    <div className="text-sm font-semibold">{h.target_role || "Resume"}</div>
                    <div className="text-xs text-zinc-500 flex items-center justify-between mt-1">
                      <span>{new Date(h.created_at).toLocaleString()}</span>
                      <span className="font-mono font-semibold text-[#0A0A0A]">{h.overall_score}/100</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card-flat p-6 lg:col-span-2 min-h-[400px]" data-testid="resume-results">
          {uploading && !analysis ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-14" data-testid="resume-analysis-loader">
              <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <Loader2 size={22} className="animate-spin text-[#0E7C66]" />
              </div>
              <div className="font-display font-bold mt-4 text-lg" data-testid="resume-progress-label">
                {progressLabel || "Starting analysis…"}
              </div>
              <p className="text-xs text-zinc-500 max-w-sm mt-1">
                Hang tight — AI analysis typically takes 60-120 seconds. We'll keep you updated.
              </p>
              <div className="mt-5 w-64 h-1.5 rounded-full bg-zinc-100 overflow-hidden" aria-hidden>
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-indigo-500 transition-[width] duration-500"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <div className="text-[11px] text-zinc-400 mt-2 font-mono">{Math.round(progress * 100)}%</div>
            </div>
          ) : !analysis ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="h-12 w-12 rounded-full bg-zinc-100 flex items-center justify-center"><FileSearch size={20} className="text-zinc-500" /></div>
              <div className="font-display font-bold mt-4 text-lg">Your analysis will appear here.</div>
              <p className="text-sm text-zinc-500 max-w-sm mt-1">Upload a resume on the left to see overall score, ATS readiness, and personalized improvements.</p>
            </div>
          ) : (
            <>
              <Results a={analysis} />
              <SummaryRewrite
                key={analysis.id}
                resumeId={analysis.resume_id || resumeId}
                analysisId={analysis.id}
                targetRole={analysis.target_role}
                baseSummary={analysis.summary_suggestion}
                approvedSummary={analysis.approved_summary}
                onApplied={(approved_summary) => {
                  const next = { ...analysis, approved_summary };
                  setAnalysis(next);
                  loadHistory();
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Subscore({ label, value, testid }) {
  const color = value >= 71 ? '#22C55E' : value >= 41 ? '#EAB308' : '#EF4444';
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-600">{label}</span>
        <span className="font-mono font-semibold">{value}</span>
      </div>
      <div className="meter-track mt-2"><div className="meter-fill" style={{ width: `${value}%`, background: color }} /></div>
    </div>
  );
}

function Section({ title, items, color = "default" }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{title}</div>
      <ul className="space-y-2">
        {items.map((t, i) => (
          <li key={i} className="text-sm flex items-start gap-2 leading-relaxed">
            {color === "success" ? <CheckCircle2 size={14} className="text-emerald-500 mt-1 shrink-0" />
              : color === "warn" ? <AlertCircle size={14} className="text-yellow-500 mt-1 shrink-0" />
              : <Sparkles size={14} className="text-[#0E7C66] mt-1 shrink-0" />}
            <span>{typeof t === 'string' ? t : JSON.stringify(t)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Results({ a }) {
  const sub = a.subscores || {};
  const radar = [
    { axis: "ATS", value: sub.ats_compatibility || 0 },
    { axis: "Format", value: sub.formatting || 0 },
    { axis: "Keywords", value: sub.keyword_optimization || 0 },
    { axis: "Skills", value: sub.skill_relevance || 0 },
    { axis: "Projects", value: sub.project_strength || 0 },
    { axis: "Industry", value: sub.industry_match || 0 },
  ];
  const ext = a.extracted || {};
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Overall Score</div>
              <div className="font-display font-black text-6xl mt-2" data-testid="overall-score">{a.overall_score}<span className="text-zinc-400 text-2xl">/100</span></div>
              <div className="text-xs text-zinc-500 mt-2">Target: {a.target_role}</div>
            </div>
            <div className="h-28 w-28 rounded-full" style={{ background: `conic-gradient(#0E7C66 ${a.overall_score}%, #F4F4F5 0)` }}>
              <div className="h-[86%] w-[86%] rounded-full bg-white scale-100 m-[7%] flex items-center justify-center font-mono font-bold">{a.overall_score}</div>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            <Subscore label="ATS Compatibility" value={sub.ats_compatibility || 0} testid="sub-ats" />
            <Subscore label="Formatting" value={sub.formatting || 0} testid="sub-format" />
            <Subscore label="Keyword Optimization" value={sub.keyword_optimization || 0} testid="sub-keyword" />
            <Subscore label="Skill Relevance" value={sub.skill_relevance || 0} testid="sub-skill" />
            <Subscore label="Project Strength" value={sub.project_strength || 0} testid="sub-project" />
            <Subscore label="Industry Match" value={sub.industry_match || 0} testid="sub-industry" />
          </div>
        </div>
        <div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radar}>
                <PolarGrid stroke="#E4E4E7" />
                <PolarAngleAxis dataKey="axis" tick={{ fill:'#52525B', fontSize:11 }} />
                <PolarRadiusAxis tick={{ fill:'#A1A1AA', fontSize:10 }} angle={30} domain={[0,100]} />
                <Radar dataKey="value" stroke="#0E7C66" fill="#0E7C66" fillOpacity={0.2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          {a.summary_suggestion && (
            <div className="mt-4 p-4 border border-[#E8DDC9] rounded-lg bg-[#FAF5E9]">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Suggested profile summary</div>
              <p className="text-sm mt-2 leading-relaxed">{a.summary_suggestion}</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6 pt-6 border-t border-zinc-200">
        <Section title="Strengths" items={a.strengths} color="success" />
        <Section title="Weaknesses" items={a.weaknesses} color="warn" />
        <Section title="Missing Keywords" items={a.missing_keywords} color="warn" />
        <Section title="ATS Recommendations" items={a.ats_recommendations} />
        <Section title="Project Suggestions" items={a.project_suggestions} />
        <Section title="Certification Suggestions" items={a.certification_suggestions} />
        <Section title="Salary Opportunities" items={a.salary_opportunities} color="success" />
      </div>

      {ext.skills && ext.skills.length > 0 && (
        <div className="mt-8 pt-6 border-t border-zinc-200">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Extracted skills</div>
          <div className="flex flex-wrap gap-2">
            {ext.skills.map((s, i) => <span key={i} className="tag">{s}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
